import {apiFetch, handleResponse, toQueryString} from "./ApiService";


const ERROR_MESSAGES = {
    fetchFail: 'Failed to fetch transactions',
};

export interface TransactionSummaryResponse {
    entries: TransactionResponse[];
    totalElements: number;
}

export interface TransactionResponse {
    transactionId: string;
    totalAmount: string;
    transactionCurrency: string;
    transactionDate: string;
    status: string;
    merchantId: string;
}

export interface TransactionDetailsResponse {
    transactionId: string;
    totalAmount: string;
    transactionCurrency: string;
    transactionDate: string;
    status: string;
    processingStatus: string;
    merchantId: string;
    isPartiallyChargeable: boolean;
    authorizations: Authorization[];
    refunds: Refund[];
    captures: Capture[];
    notificationLogs: NotificationLog[];
}

export interface TransactionFiltersResponse {
    transactionId: string[];
    transactionCurrency: string[];
    transactionDate: string[];
    status: string[];
    merchantId: string[];
    sort: string[];
}


export interface TransactionRequest {
    search?: string;
    transactionDateFrom?: string;
    transactionDateTo?: string;
    currency?: string;
    status?: string
    merchantId?: string;
    processingStatus?: string;
    totalAmountMin?: string;
    totalAmountMax?: string;
    page?: number;
    size?: number;
    sort?: string[];
}

export interface FilterRequest {
    search?: string;
    transactionDateFrom?: string;
    transactionDateTo?: string;
    currency?: string;
    status?: string
    merchantId?: string;
    processingStatus?: string;
    totalAmountMin?: string;
    totalAmountMax?: string;
    sort?: string[];
}

export interface Authorization {
    authorizationId: string;
    authorizationAmount: string;
    authorizedCurrency: string;
    authorizationDate: string;
    pspReference: string;
    originalReference: string;
}

export interface Refund {
    refundId: string;
    refundedAmount: string;
    refundDate: string;
    pspReference: string;
    originalReference: string;
    exportStatus: string;
}

export interface Capture {
    captureId: string;
    capturedAmount: string;
    captureDate: string;
    pspReference: string;
    originalReference: string;
    directCapture: boolean;
    exportStatus: string;
}

export interface NotificationLog {
    uuid: string;
    amountValue: string;
    amountCurrency: string;
    eventCode: string;
    eventDate: string;
    success: boolean;
    pspReference: string;
}

export interface NotificationLogDetailsResponse {
    uuid: string;
    amountValue: string;
    amountCurrency: string;
    eventCode: string;
    eventDate: string;
    merchantAccountCode: string;
    merchantReference: string;
    originalReference: string;
    pspReference: string;
    transactionReference: string;
    reason: string;
    success: boolean;
    paymentMethod: string;
    additionalData: string;
    createdAt: string;
    processedAt: string;
}

export const fetchTransactions = async (request: TransactionRequest):
    Promise<TransactionSummaryResponse> => {
    const qs = toQueryString(request);
    const response = await apiFetch(`api/transactions${qs ? `?${qs}` : ""}`, {method: 'GET'});
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response.json();
}

export const getTransactionDetails = async (
    transactionId: string
): Promise<TransactionDetailsResponse> => {
    const response = await apiFetch(
        `/api/transactions/${transactionId}`,
        {method: "GET"}
    );
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response.json();
};

export const getTransactionFilters = async (): Promise<TransactionFiltersResponse> => {
    const response = await apiFetch(
        `/api/transactions/filters`,
        {method: "GET"}
    );
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response.json();
}

export const getNotificationLog = async (notificationId: string): Promise<NotificationLogDetailsResponse> => {
    const response = await apiFetch(
        `/api/transactions/notification-logs/${notificationId}`,
        {method: "GET"}
    );
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response.json();
}

export const downloadTransactionsToCSV = async (request: FilterRequest): Promise<Response> => {
    const qs = toQueryString(request);
    const response = await apiFetch(`/api/transactions/download${qs ? `?${qs}` : ""}`, {method: 'GET'});
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return response;
}